import { Chip, Stack, Typography } from "@mui/material";
import { WasteStreamRegistrationStatus } from "generated-graphql/graphql";
import { ReactNode } from "react";

import { prettyPrintWasteStreamRegistrationStatus } from "./Facilities/Facility/prettyPrintWasteStreamRegistrationStatus";

// Semantic, low-contrast outlined chips per the design system (never solid). The
// status carries the state; the registered ID (or a staff submission action)
// sits inline after it so the cell stays within the grid's fixed row height.
const STATUS_COLOR: Record<WasteStreamRegistrationStatus, "success" | "info" | "default"> = {
  [WasteStreamRegistrationStatus.Registered]: "success",
  [WasteStreamRegistrationStatus.InProgress]: "info",
  [WasteStreamRegistrationStatus.NotRegistered]: "default",
};

interface WasteStreamRegistrationCellProps {
  status: WasteStreamRegistrationStatus | null | undefined;
  registeredId: string | null | undefined;
  /**
   * Staff-only create/open-submission action, shown beneath the status chip when
   * the stream has no registered ID yet. Omitted for non-staff / read-only.
   */
  submissionAction?: ReactNode;
}

/**
 * Combined "Registration" cell: the registration status as a chip with the
 * state-assigned ID (or a staff submission action) inline to its right — folding
 * the former separate "State Registered ID" and "Registration Status" columns
 * into one that reads as a single piece of information. Laid out on one line so
 * the cell fits the grid's fixed row height; the ID truncates if the column is
 * narrow.
 */
export function WasteStreamRegistrationCell({
  status,
  registeredId,
  submissionAction,
}: WasteStreamRegistrationCellProps) {
  const label = prettyPrintWasteStreamRegistrationStatus(status);

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      {label && (
        <Chip
          label={label}
          size="small"
          variant="outlined"
          color={status ? STATUS_COLOR[status] : "default"}
          sx={{ flexShrink: 0 }}
        />
      )}
      {registeredId ? (
        <Typography
          variant="body2"
          sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {registeredId}
        </Typography>
      ) : (
        submissionAction
      )}
    </Stack>
  );
}
